import type { Feature } from 'ol';
import type { Geometry } from 'ol/geom';
import { Point } from 'ol/geom';
import type { DrawEvent } from 'ol/interaction/Draw';
import Draw from 'ol/interaction/Draw';

import { DrawToolType } from '@nora-monorepo/nora-api-client';

import { CreateFeatureBaseDrawTool } from '#nora-ui/map/drawtools/create-feature-base-draw-tool';
import useTextToolStore from '#nora-ui/map/drawtools/stores/text-tool-store';
import { getFeatureStyle } from '#nora-ui/map/drawtools/styles/styles';
import { createTextToolFontStyle, getFontSize } from '#nora-ui/map/drawtools/styles/text-tool-style';
import type { DrawToolOptions } from '#nora-ui/map/drawtools/types';
import { extractDrawToolOptions } from '#nora-ui/map/drawtools/types';
import { getFeatureProperties } from '#nora-ui/map/feature-properties';
import type { DrawToolManager } from '#nora-ui/map/map/DrawToolManager';

export class TextTool extends CreateFeatureBaseDrawTool {
  drawToolType: DrawToolType = DrawToolType.text;
  options: DrawToolOptions = {
    drawToolType: this.drawToolType,
  };

  textToolStore = useTextToolStore();

  // The default text label text
  private readonly defaultTextLabel = 'Text';

  private readonly drawInteraction: Draw;

  // When false we use the default text label, otherwise we know we have changed the text label.
  private hasUpdatedTextLabel = false;

  constructor(drawToolManager: DrawToolManager) {
    super(drawToolManager);
    this.drawToolName = 'Text';
    this.drawInteraction = new Draw({
      source: this.editingVectorSource,
      type: 'Point',
      style: {
        'circle-radius': 4,
        'circle-fill-color': '#ffffff',
        'circle-stroke-color': '#000000',
      },
    });

    this.drawInteraction.on('drawstart', this.onDrawStart.bind(this));
    this.drawInteraction.on('drawend', this.onDrawEnd.bind(this));
  }

  override activate(): void {
    super.activate();
    const map = this.drawToolManager.map.getOlMapOrThrow();
    map.addInteraction(this.drawInteraction);
  }

  override deactivate(): void {
    super.deactivate();
    const map = this.drawToolManager.map.getOlMapOrThrow();
    map.removeInteraction(this.drawInteraction);
    this.hasUpdatedTextLabel = false;
    this.textToolStore.reset();
  }

  /**
   * Overriding load feature to set the existing text tool styling and text label upon editing
   * @param {Feature<Geometry>} feature
   */
  override loadFeature(feature: Feature<Geometry>): void {
    const featureProperties = extractDrawToolOptions(feature);
    this.drawToolManager.updateDrawStyleOptions(featureProperties);

    const { font, labelText } = getFeatureProperties<{ font?: string; labelText?: string }>(feature);
    this.textToolStore.fontSize = getFontSize(font);

    if (typeof labelText === 'string') {
      this.textToolStore.text = labelText;
      this.hasUpdatedTextLabel = true;
    }
    else {
      this.textToolStore.text = '';
      this.hasUpdatedTextLabel = false;
    }

    super.loadFeature(feature);
  }

  override showLineColor(): boolean {
    return false;
  }

  override showFillColor(): boolean {
    return false;
  }

  override showStyles(): boolean {
    return false;
  }

  onDrawStart(): void {
    this.editingVectorSource.clear();
  }

  onDrawEnd(event: DrawEvent): void {
    const existingFeatureId = this.feature?.get('id') as string | undefined;
    const coordinate = (event.feature.getGeometry() as Point).getCoordinates();

    this.feature = event.feature;
    this.feature.setGeometry(new Point(coordinate));

    if (existingFeatureId !== undefined) {
      this.featureId = existingFeatureId;
      this.feature.set('id', existingFeatureId);
    }

    this.onStylingUpdated();
  }

  onTextUpdated(): void {
    this.hasUpdatedTextLabel = true;
    this.onStylingUpdated();
  }

  onFontSizeUpdated(): void {
    this.onStylingUpdated();
  }

  getText(): string {
    if (this.hasUpdatedTextLabel) {
      return this.textToolStore.text;
    }

    if (this.textToolStore.text.length === 0) {
      // Default value so the user sees something before specifying text themselves
      return this.defaultTextLabel;
    }
    return this.textToolStore.text;
  }

  override onStylingUpdated(): void {
    super.onStylingUpdated();
    if (!this.feature) {
      return;
    }

    this.feature.setProperties({
      ...this.getDefaultProperties(),
      font: createTextToolFontStyle(this.textToolStore.fontSize),
      labelText: this.getText(),
      nameVisible: false,
    });
    this.feature.setStyle(getFeatureStyle);
    this.markDirty();
  }
}
